import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import {
  useBillingServiceGetV1BillingPrices,
  useBillingServicePostV1BillingCheckout,
  useBillingServicePostV1BillingPortal,
} from '../client/queries'

/**
 * Where Stripe sends the browser back to.
 *
 * Success gets a page of its own. Payment finishes before the webhook has written the
 * entitlement, so returning to an ordinary page would show one that has not changed yet —
 * indistinguishable from a payment that failed. /billing/done exists to hold that gap and
 * say what is happening.
 *
 * Cancelling has nothing to report, so it goes home.
 *
 * Absolute, because these are handed to Stripe rather than to the router, and built from
 * the live origin so the same code works on localhost, dev and prod with no config.
 */
const returnUrls = () => ({
  cancelUrl: `${window.location.origin}/home`,
  returnUrl: `${window.location.origin}/home`,
  // Stripe substitutes the id. The braces are its template syntax and must survive
  // un-encoded.
  successUrl: `${window.location.origin}/billing/done?checkout={CHECKOUT_SESSION_ID}`,
})

/**
 * Opens Stripe Checkout.
 *
 * No cache work on success, deliberately: the answer is a redirect, and this tab is on
 * its way to Stripe. What matters to our state happens later, in the webhook, and reaches
 * the client when it comes back and refetches.
 *
 * The redirect is a plain assignment — hosted Checkout hands back a URL, so there is no
 * Stripe library on this side and nothing to initialise.
 */
export const useStartCheckout = () => {
  const { t } = useTranslation()

  const mutation = useBillingServicePostV1BillingCheckout({
    onError: () => toast.error(t('billing.checkoutFailed')),
    onSuccess: ({ url }) => {
      window.location.href = url
    },
  })

  return {
    ...mutation,
    start: (params: {
      individualIds: string[]
      interval: 'month' | 'year'
      recurring: boolean
    }) => {
      const { cancelUrl, successUrl } = returnUrls()

      return mutation.mutateAsync({ requestBody: { ...params, cancelUrl, successUrl } })
    },
  }
}

/**
 * Opens the Stripe Customer Portal — cancel, change card, change how many individuals
 * are paid for.
 *
 * All of that is Stripe's own UI rather than ours, which is why there is nothing here
 * but a redirect. A 404 means nothing has ever been bought, so there is no billing to
 * manage and the button that led here should not have been shown.
 */
export const useOpenBillingPortal = () => {
  const { t } = useTranslation()

  const mutation = useBillingServicePostV1BillingPortal({
    onError: () => toast.error(t('billing.portalFailed')),
    onSuccess: ({ url }) => {
      window.location.href = url
    },
  })

  return {
    ...mutation,
    open: () => mutation.mutateAsync({ requestBody: { returnUrl: returnUrls().returnUrl } }),
  }
}

/**
 * The plans on offer, and what adding an individual to an existing subscription costs.
 *
 * Takes the global `staleTime` rather than a longer one of its own. A Price cannot change
 * — repricing creates a new one — so the prices alone would happily cache for hours, but
 * the response also carries `addSeat` and an `amount` computed for THIS owner, and both
 * move the moment they buy something.
 *
 * That combination had a sharp edge: the query cache is persisted to IndexedDB, so it
 * survives the round trip out to Stripe and back. Subscribing for one individual and then
 * opening this dialog for a second served the pre-purchase answer from disk — full price
 * on screen, reduced price actually charged, and for a recurring plan the ordinary plan
 * picker shown where the add-a-seat confirmation belonged. `/billing/done` now invalidates
 * this on arrival, which is the moment the answer is known to have changed; the shorter
 * window is what covers every other way back.
 */
export const usePlanOptions = () => useBillingServiceGetV1BillingPrices()
